import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Breadcrumb as BreadcrumbStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(BreadcrumbStyles, 'breadcrumb')

type Props = PropsWithChildren<{
  /** Specify the label for the breadcrumb `nav` container */
  'aria-label'?: string
}>

export type BreadcrumbProps = Props & CommonProps

const Breadcrumb = (props: BreadcrumbProps) => {
  const { 'aria-label': ariaLabel = 'breadcrumb', className, children, ...rest } = props
  const classes = useStyles()

  return (
    <nav aria-label={ariaLabel}>
      <ol className={clsx(classes.root, className)} {...rest}>
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return null
          if (child.type === React.Fragment) {
            console.warn(
              "[AIRBUS-DS] The Breadcrumb component doesn't accept a Fragment as a child. Consider providing an array instead.",
            )
          } else if (child.type !== BreadcrumbItem) {
            console.warn('[AIRBUS-DS] The Breadcrumb component only accept BreadcrumbItem as children.')
          }
          return child
        })}
      </ol>
    </nav>
  )
}

export default Breadcrumb

// retrocompat : Deprecated component name
export const Breadcrumbs = ({ children, ...rest }: BreadcrumbProps) => {
  useDeprecatedComponent('Breadcrumbs', 'Please use Breadcrumb.')

  const childrenCount = React.Children.count(children)
  const wrappedChildren = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return null

    // automatically wrap childs in BreadcrumbItem
    if (child.type !== BreadcrumbItem) {
      return <BreadcrumbItem current={index === childrenCount - 1}>{child}</BreadcrumbItem>
    }
  })
  return <Breadcrumb children={wrappedChildren} {...rest} />
}

export type BreadcrumbItemProps = PropsWithChildren<{
  /** Provide if this item represents the current page */
  current?: boolean
}> &
  CommonProps

export const BreadcrumbItem = (props: BreadcrumbItemProps) => {
  const { children, className, current, ...rest } = props
  const classes = useStyles()

  return (
    <li
      className={clsx(className, classes.item, {
        [classes.item_current]: current,
      })}
      {...rest}
    >
      {children}
    </li>
  )
}
